import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Search, FileText, Loader2, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import PreSignatureUpload from "../components/documents/PreSignatureUpload";
import { SEVERITY_PILL_CLASS, SEVERITY_RANK } from "../components/ClauseFindings";
import { VERDICT_SLUG, VERDICT_TONE, verdictKey } from "./PreSignatureDetail";
import {
  ELIGIBLE_TYPES, isEligibleDocument, buildRows, filterRows, unavailableMessageKey,
  eligibleTypesLabel,
} from "./preSignatureRows";

/**
 * Hub "Avant signature" — liste des analyses pre-signature de l'espace, plus
 * un lanceur mono-document. Lit l'endpoint decisions existant filtre sur le
 * mode should_i_sign : aucune route backend dediee.
 *
 * Les helpers purs (ELIGIBLE_TYPES, isEligibleDocument, buildRows, filterRows,
 * unavailableMessageKey) vivent dans ./preSignatureRows.js, sans import
 * react-router-dom, pour rester testables en isolation (cf. PreSignatureDetail
 * / preSignatureVerdict.js pour le meme motif).
 */
export {
  ELIGIBLE_TYPES, isEligibleDocument, buildRows, filterRows, unavailableMessageKey,
  eligibleTypesLabel,
};

/** Lance une analyse avant signature sur UN document. Reutilise par le
 *  lanceur secondaire de la page Analyse (cf. Task 12). */
export async function runAnalysis(docId) {
  const res = await api.post("/decisions", {
    mode: "should_i_sign", subject_doc_ids: [docId],
  });
  return res.data;
}

function Launcher({ documents, onDone }) {
  const { t } = useTranslation("pre_signature");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [docId, setDocId] = useState("");
  const [running, setRunning] = useState(false);
  const candidates = useMemo(() => documents.filter(isEligibleDocument), [documents]);

  const run = async () => {
    if (!docId) return;
    setRunning(true);
    try {
      const decision = await runAnalysis(docId);
      toast.success(t("toasts.ready"));
      setOpen(false);
      onDone?.();
      navigate(`/pre-signature/${decision.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const key = unavailableMessageKey(detail);
      toast.error(key
        ? t(key, { types: eligibleTypesLabel(t) })
        : (formatApiErrorDetail(detail) || t("toasts.failed")));
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-testid="presig-launcher-button"
        className="bg-zinc-950 hover:bg-zinc-800 text-white gap-1.5 h-9"
      >
        <ShieldCheck size={14} strokeWidth={1.75} /> {t("launcher.cta")}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
          data-testid="presig-launcher-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <h2 className="font-display text-xl font-semibold text-zinc-950">
                  {t("launcher.title")}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">{t("launcher.subtitle")}</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="presig-launcher-close"
                className="w-7 h-7 grid place-items-center rounded hover:bg-zinc-100"
              >
                <X size={14} />
              </button>
            </div>

            {candidates.length === 0 ? (
              <p className="text-xs text-zinc-500 italic" data-testid="presig-launcher-empty">
                {t("launcher.no_candidates", { types: eligibleTypesLabel(t) })}
              </p>
            ) : (
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
                  {t("launcher.pick_document")}
                </span>
                <select
                  value={docId}
                  onChange={(e) => setDocId(e.target.value)}
                  data-testid="presig-launcher-select"
                  className="mt-2 w-full h-9 px-2 rounded-lg border border-zinc-200 bg-white text-sm"
                >
                  <option value="">—</option>
                  {candidates.map((d) => (
                    <option key={d.id} value={d.id}>{d.filename}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-zinc-200">
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="presig-launcher-cancel">
                {t("launcher.cancel")}
              </Button>
              <Button
                onClick={run}
                disabled={running || !docId}
                data-testid="presig-launcher-run"
                className="bg-zinc-950 hover:bg-zinc-800 text-white gap-1.5"
              >
                {running
                  ? <><Loader2 size={13} className="animate-spin" /> {t("launcher.running")}</>
                  : t("launcher.run")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function PreSignature() {
  const { t, i18n } = useTranslation("pre_signature");
  const [decisions, setDecisions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("All");

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/decisions", { params: { mode: "should_i_sign", limit: 200 } }),
      api.get("/documents", { params: { limit: 200 } }),
    ])
      .then(([dec, docs]) => {
        setDecisions(dec.data || []);
        setDocuments(docs.data || []);
      })
      .catch((e) => toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || t("toasts.list_failed")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const docsById = useMemo(
    () => Object.fromEntries(documents.map((d) => [d.id, d])), [documents]);
  const rows = useMemo(() => buildRows(decisions, docsById), [decisions, docsById]);
  const visible = useMemo(() => filterRows(rows, { search, severity }), [rows, search, severity]);

  return (
    <div className="space-y-6" data-testid="presig-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
            {t("page.title")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">{t("page.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <PreSignatureUpload />
          <Launcher documents={documents} onDone={load} />
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("page.search_placeholder")}
            data-testid="presig-search"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-zinc-200 bg-white text-sm"
          />
        </div>
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          data-testid="presig-severity-filter"
          aria-label={t("page.filter_severity")}
          className="h-9 px-2 rounded-lg border border-zinc-200 bg-white text-sm"
        >
          <option value="All">{t("page.filter_all")}</option>
          {Object.keys(SEVERITY_RANK).map((s) => (
            <option key={s} value={s}>{t(`severity.${s}`)}</option>
          ))}
        </select>
        <span className="text-xs text-zinc-500" data-testid="presig-count">
          {t("page.count", { count: visible.length })}
        </span>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center" data-testid="presig-loading">
          <Loader2 size={18} className="animate-spin text-zinc-400" />
        </div>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center" data-testid="presig-empty">
          <p className="font-display font-semibold text-zinc-900">{t("page.empty_title")}</p>
          <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">{t("page.empty_body", { types: eligibleTypesLabel(t) })}</p>
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-left">
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t("page.col_document")}</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t("page.col_verdict")}</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t("page.col_clauses")}</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t("page.col_worst")}</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t("page.col_date")}</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {visible.map((r) => (
                <tr key={r.id} data-testid={`presig-row-${r.id}`} className="hover:bg-zinc-50">
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText size={13} className="text-zinc-400 shrink-0" strokeWidth={1.75} />
                      <span className="truncate font-medium text-zinc-950">{r.filename}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${VERDICT_TONE[VERDICT_SLUG[r.verdict] || "unknown"]}`}>
                      {t(verdictKey(r.verdict))}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-zinc-700">{r.clauseCount}</td>
                  <td className="px-4 py-2.5">
                    {r.worst ? (
                      <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${SEVERITY_PILL_CLASS[r.worst]}`}>
                        {t(`severity.${r.worst}`)}
                      </span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500 text-xs">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString(i18n.language) : "—"}
                  </td>
                  <td className="px-2 py-2.5">
                    <Link
                      to={`/pre-signature/${r.id}`}
                      data-testid={`presig-open-${r.id}`}
                      className="text-zinc-400 hover:text-zinc-900 inline-flex"
                      aria-label={r.filename}
                    >
                      <ArrowUpRight size={15} strokeWidth={2} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
